import { TeeColor, useGetClubQuery, CoordinateSystem } from '@slope/graphql/apollo';
import {
  Button,
  Flex,
  HoleSelector,
  HoleSelectorMode,
  IconButton,
  Typography,
  useHoleSelector,
  useMutableCallback,
} from '@slope/uikit';
import styled from 'styled-components';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { CharacterWholeNumber, CloseOutline, SkipBack } from '@carbon/icons-react';
import { useSpring, a, config } from '@react-spring/web';
import { createStore, StoreProvider } from '@slope/store';
import * as THREE from 'three';
import { Viewer } from '../components';
import { HoleInfo } from '../components/HoleInfo';
import { useControls } from 'leva';

const AnimatedFlex = a(Flex);

const ClubInfoCard = styled.div.attrs<{ 'data-club-slug'?: string; isExpanded?: boolean }>(
  (props) => ({
    'data-club-slug': props['data-club-slug'],
  }),
)<{ 'data-club-slug'?: string; isExpanded?: boolean }>`
  background: ${(props) => (props.isExpanded ? 'white' : 'transparent')};
  border-radius: ${(props) => (props.isExpanded ? '16px' : '12px')};
  padding: ${(props) => (props.isExpanded ? '2rem 2.5rem' : '1rem 1.5rem')};
  box-shadow: ${(props) => (props.isExpanded ? '0 4px 6px rgba(0, 0, 0, 0.1)' : 'none')};
  position: absolute;
  top: ${(props) => (props.isExpanded ? '50%' : '1rem')};
  left: ${(props) => (props.isExpanded ? '50%' : '1rem')};
  transform: ${(props) => (props.isExpanded ? 'translate(-50%, -50%)' : 'none')};
  z-index: 999;
  pointer-events: all;
  transition: all 0.3s ease;
  min-width: ${(props) => (props.isExpanded ? '400px' : 'auto')};
  max-width: ${(props) => (props.isExpanded ? '600px' : 'auto')};

  @supports (view-transition-name: none) {
    view-transition-name: ${(props) =>
      props['data-club-slug'] ? `club-card-${props['data-club-slug']}` : 'none'};
  }
`;

const ClubCardContent = styled.div<{ isExpanded?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.isExpanded ? '1rem' : '0.25rem')};
`;

const Container = styled(Flex)`
  flex-direction: row;
  gap: 1rem;
  margin: 1rem;
  position: absolute;
  justify-content: space-between;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  z-index: 1000;
  pointer-events: none;
  align-items: end;
  max-width: 640px;
  /* Add safe area padding for iOS/iPad bottom controls */
  padding-bottom: env(safe-area-inset-bottom, 0px);
  margin-bottom: max(1rem, env(safe-area-inset-bottom, 1rem));

  & > * {
    pointer-events: all;
  }
`;

export const ClubView: React.FC = () => {
  const { clubSlug, holeNumber } = useParams<{ clubSlug?: string; holeNumber?: string }>();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showHoleInfo, setShowHoleInfo] = useState(false);

  const clubResult = useGetClubQuery({
    skip: !clubSlug,
    variables: {
      by: {
        slug: clubSlug!,
      },
    },
  });

  const availableHoles = useMemo(() => clubResult?.data?.club.holes ?? [], [clubResult.data]);

  const availableHoleNames = useMemo(
    () => availableHoles.map(({ number }) => `hole_${number}`),
    [availableHoles],
  );

  const selectedHole = useMemo(() => {
    if (holeNumber) {
      return availableHoles.find((hole) => hole.number === parseInt(holeNumber)) || null;
    }
    return null;
  }, [availableHoles, holeNumber]);

  const [state, { select }] = useHoleSelector({
    selection: new Set(holeNumber ? [parseInt(holeNumber)] : []),
    mode: HoleSelectorMode.Limited,
    limit: 1,
  });

  const handleHoleChange = useCallback(
    (hole: number) => {
      select(hole);
    },
    [select],
  );

  const handleConfirmSelection = useCallback(() => {
    const selected = Array.from(state.selection)[0];
    if (clubSlug && selected) {
      navigate(`/${clubSlug}/${selected}`);
      setShowHoleInfo(true);
    }
  }, [clubSlug, state.selection, navigate]);

  const toggleHoleSelector = useMutableCallback(() => {
    setOpen((prev) => !prev);
  });

  const handleGoHome = useMutableCallback(() => {
    if (clubSlug) {
      navigate(`/`, { replace: true, viewTransition: true });
    }
  });

  useEffect(() => {
    if (holeNumber) {
      handleConfirmSelection();
    }
  }, [holeNumber, handleConfirmSelection]);

  const origin = {
    lat: 37.35209385948764,
    lng: -5.9199256092221955,
  };

  const [store] = useState(() =>
    createStore({
      scene: new THREE.Object3D(),
      origin: origin,
      controls: null,
      green: {
        latitude: 0,
        longitude: 0,
        system: CoordinateSystem.Wgs84,
      },
      playerTee: {
        color: TeeColor.Black,
        coordinates: {
          latitude: 0,
          longitude: 0,
          system: CoordinateSystem.Wgs84,
        },
      },
      selectedHole: selectedHole as any,
      availableHoles: availableHoles as any,
    }),
  );

  useEffect(() => {
    store.setState({ selectedHole: selectedHole as any, availableHoles: availableHoles as any });
  }, [selectedHole, availableHoles, store]);

  const holeSelector = (
    <HoleSelector
      state={state}
      onHoleChange={handleHoleChange}
      totalHoles={availableHoleNames.length}
    />
  );

  const springProps = useSpring({
    opacity: open ? 1 : 0,
    transform: open ? 'translateY(0)' : 'translateY(100%)',
    height: open ? 'auto' : '0px',
    config: config.stiff,
  });

  // Tee selector controls
  const availableTeeColors = useMemo(() => {
    const colors = new Set<TeeColor>();
    selectedHole?.tees.forEach(({ color }) => colors.add(color));
    return Array.from(colors).sort();
  }, [selectedHole]);

  const { teeColor } = useControls(
    'Tee Selector',
    {
      teeColor: {
        value: availableTeeColors[0] || TeeColor.Black,
        options: availableTeeColors,
      },
    },
    { collapsed: false },
    [availableTeeColors, selectedHole],
  );

  const selectedTee = useMemo(() => {
    return selectedHole?.tees.find(({ color }) => color === teeColor) ?? null;
  }, [selectedHole, teeColor]);

  return (
    <StoreProvider store={store}>
      <Flex height="100vh" width="100vw" position="relative" overflow="hidden">
        {/* Club Info Card - always show club name, full card when no hole selected */}
        {clubSlug && clubResult.data?.club && (
          <ClubInfoCard data-club-slug={clubSlug} isExpanded={!selectedHole}>
            <ClubCardContent isExpanded={!selectedHole}>
              <Typography
                fontSize={selectedHole ? '24px' : '32px'}
                fontWeight="bold"
                color="#2c3e50"
              >
                {clubResult.data.club.name}
              </Typography>
              {!selectedHole && (
                <>
                  <Typography fontSize="16px" color="#7f8c8d">
                    📍 {clubResult.data.club.country}
                  </Typography>
                  <div style={{ marginTop: '1rem' }}>
                    {holeSelector}
                    <Button
                      fullWidth
                      onClick={handleConfirmSelection}
                      disabled={state.selection.size === 0}
                      style={{ marginTop: '1rem' }}
                    >
                      Explorar Hoyo
                    </Button>
                  </div>
                </>
              )}
            </ClubCardContent>
          </ClubInfoCard>
        )}

        {/* Hole Info - shown when hole is selected */}
        {selectedHole && showHoleInfo && (
          <Flex position="absolute" top="1rem" right="1rem" zIndex={100} gap="1rem">
            <HoleInfo
              key={selectedHole.id}
              number={selectedHole.number ?? '?'}
              par={selectedHole.par ?? '?'}
              strokeIndex={selectedHole.strokeIndex ?? '?'}
              distance={selectedTee?.length ?? '?'}
              color={selectedTee?.color ?? TeeColor.Black}
            />
          </Flex>
        )}

        {/* Viewer - always mounted once club is loaded */}
        {selectedHole && clubSlug && <Viewer hole={selectedHole} clubSlug={clubSlug} />}

        {/* Bottom controls when hole selected */}
        {selectedHole && (
          <Container>
            <IconButton onClick={handleGoHome}>
              <SkipBack />
            </IconButton>
            <AnimatedFlex flexDirection="column" style={springProps} width="100%">
              {holeSelector}
            </AnimatedFlex>
            <IconButton onClick={toggleHoleSelector}>
              {open ? <CloseOutline /> : <CharacterWholeNumber />}
            </IconButton>
          </Container>
        )}
      </Flex>
    </StoreProvider>
  );
};
